import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'March 4, 2026';

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)', marginBottom: 12, letterSpacing: 0.5 }}>
        {title}
      </h2>
      <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: 15 }}>
        {children}
      </div>
    </div>
  );
}

export default function TermsOfService() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', padding: '48px 24px 80px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
            Legal
          </p>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>
            Terms of Service
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            Last updated: {LAST_UPDATED}
          </p>
          <div style={{ height: 1, background: 'var(--border)', marginTop: 24 }} />
        </div>

        <Section title="1. Acceptance of Terms">
          <p>
            By accessing or using NeuroRank ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Platform.
          </p>
          <p style={{ marginTop: 12 }}>
            NeuroRank is a cognitive assessment platform designed for the esports community. It provides psychomotor and cognitive test batteries and generates shareable performance profiles.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>You must be at least <strong style={{ color: 'var(--text-primary)' }}>13 years of age</strong> to use NeuroRank. If you are under 18, you represent that a parent or guardian has reviewed and agreed to these terms on your behalf.</p>
          <p style={{ marginTop: 12 }}>
            By creating an account or submitting a test session, you confirm that you meet this age requirement.
          </p>
        </Section>

        <Section title="3. Your Profile and Test Data">
          <p>When you complete a test session, NeuroRank generates a public neural profile accessible via a unique shareable link (e.g., neurorank.app/profile/NR-XXXXXX). By submitting a session you acknowledge that:</p>
          <ul style={{ marginTop: 12, paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Your scores, archetype, and player tag are <strong style={{ color: 'var(--text-primary)' }}>publicly visible by default</strong> to anyone with the link, including esports scouts and organisations.</li>
            <li>Your profile may appear in the Scout Dashboard and be discoverable by registered scout accounts.</li>
            <li>You may claim your profile by creating an account, which gives you control over visibility settings.</li>
          </ul>
        </Section>

        <Section title="4. Score Accuracy and Limitations">
          <p style={{ padding: '12px 16px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, color: 'var(--warning)', marginBottom: 12 }}>
            NeuroRank scores are cognitive performance estimates, not definitive measures of gaming ability or professional potential.
          </p>
          <p>Norms are currently based on established cognitive science literature and will recalibrate as real user data accumulates. Scores should be interpreted as relative indicators, not absolute benchmarks. NeuroRank makes no guarantee that scores correlate with in-game rank or professional performance.</p>
          <p style={{ marginTop: 12 }}>Test results can be affected by hardware (mouse, monitor), internet latency, environment (ambient noise, fatigue), and browser performance. Results are most meaningful when taken under consistent, controlled conditions.</p>
        </Section>

        <Section title="5. Test Integrity">
          <p>You agree to complete all test modules honestly and without assistance, automation, or manipulation. Prohibited conduct includes:</p>
          <ul style={{ marginTop: 12, paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Using scripts, macros, or automated input tools during testing</li>
            <li>Switching tabs, minimising the browser, or otherwise leaving the test window during an active module</li>
            <li>Retaking tests repeatedly in a short period to artificially inflate scores</li>
            <li>Sharing session links or impersonating another player</li>
          </ul>
          <p style={{ marginTop: 12 }}>Profiles suspected of containing fraudulent scores may be flagged or removed without notice.</p>
        </Section>

        <Section title="6. User Accounts">
          <p>Creating an account is optional but required to claim a profile, access the shortlist, and use scout features. You are responsible for:</p>
          <ul style={{ marginTop: 12, paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Keeping your password secure and not sharing account access</li>
            <li>All activity that occurs under your account</li>
            <li>Providing accurate information when registering</li>
          </ul>
          <p style={{ marginTop: 12 }}>We reserve the right to suspend or terminate accounts that violate these terms.</p>
        </Section>

        <Section title="7. Scout Accounts and Player Discovery">
          <p>Scout accounts may search, filter, bookmark, and compare player profiles. Scouts agree that player profile data is provided for <strong style={{ color: 'var(--text-primary)' }}>legitimate talent evaluation purposes only</strong>. Bulk scraping, reselling, or redistribution of profile data is prohibited.</p>
          <p style={{ marginTop: 12 }}>Players who have not enabled "Open to Opportunities" should not be contacted unsolicited through external platforms based solely on their NeuroRank profile.</p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>All content, design, code, test methodology, scoring algorithms, and archetypes on NeuroRank are the intellectual property of NeuroRank and its creators. You may not copy, reproduce, or distribute any part of the Platform without written permission.</p>
          <p style={{ marginTop: 12 }}>Your player tag and test data remain yours. By submitting a session, you grant NeuroRank a non-exclusive licence to display, aggregate, and use your anonymised performance data to improve norm calibration and platform quality.</p>
        </Section>

        <Section title="9. Disclaimers and Limitation of Liability">
          <p>THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. NEURORANK DOES NOT GUARANTEE UNINTERRUPTED ACCESS, ERROR-FREE RESULTS, OR ANY SPECIFIC OUTCOME FROM USING THE PLATFORM.</p>
          <p style={{ marginTop: 12 }}>TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEURORANK SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE PLATFORM, INCLUDING BUT NOT LIMITED TO MISSED SCOUTING OPPORTUNITIES OR RELIANCE ON TEST SCORES FOR PROFESSIONAL DECISIONS.</p>
        </Section>

        <Section title="10. Changes to These Terms">
          <p>We may update these Terms of Service at any time. Continued use of the Platform after changes are posted constitutes acceptance of the updated terms. We will update the "Last updated" date at the top of this page when changes are made.</p>
        </Section>

        <Section title="11. Contact">
          <p>If you have questions about these terms, please reach out via the contact information on our platform or open an issue at our public repository.</p>
        </Section>

        {/* Footer nav */}
        <div style={{ height: 1, background: 'var(--border)', margin: '40px 0 32px' }} />
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Link to="/" style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}>← Back to Home</Link>
          <Link to="/privacy" style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}>Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}
